import { type FunctionComponent } from 'preact'
import { html } from 'htm/preact'
import { signal, computed } from '@preact/signals'
import '@substrate-system/input'
import '@substrate-system/button'
import type { AppState } from '../../state.js'
import {
    ToolPage,
    Disclaimer,
    ToolWorkbench,
} from '../../components/tool-page.js'
import { calcLeakRate, type ApplianceType } from '../../util/leak-rate.js'
import { EM_DASH } from '../../constants.js'
import './leak-rate-calculator.css'

type Field = 'refrigerantAdded'|'fullSystemCharge'|'daysSinceLast'
type Errors = Partial<Record<Field, string>>

const applianceType = signal<ApplianceType>('commercial')
const refrigerantAdded = signal<string>('')
const fullSystemCharge = signal<string>('')
const daysSinceLast = signal<string>('')
const submitted = signal(false)
/* Bumped on reset to remount the substrate-input web components, which
   do not sync a programmatic value change back to their inner input. */
const formNonce = signal(0)

const APPLIANCE_LABELS:Record<ApplianceType, string> = {
    commercial: 'Commercial refrigeration',
    industrial: 'Industrial process refrigeration',
    comfort: 'Comfort cooling',
    other: 'Other',
}

function validate ():Errors {
    const errors:Errors = {}
    const added = parseFloat(refrigerantAdded.value)
    const charge = parseFloat(fullSystemCharge.value)
    const days = parseFloat(daysSinceLast.value)

    if (refrigerantAdded.value.trim() === '' || isNaN(added) || added < 0) {
        errors.refrigerantAdded = 'Enter the pounds added (0 or more).'
    }
    if (fullSystemCharge.value.trim() === '' || isNaN(charge) || charge <= 0) {
        errors.fullSystemCharge = 'Enter the full charge in pounds.'
    }
    if (daysSinceLast.value.trim() === '' || isNaN(days) || days < 1) {
        errors.daysSinceLast = 'Enter the number of days (1 or more).'
    }
    return errors
}

const errors = computed<Errors>(() => (submitted.value ? validate() : {}))

const result = computed(() => {
    if (!submitted.value) return null
    if (Object.keys(validate()).length > 0) return null
    return calcLeakRate({
        applianceType: applianceType.value,
        refrigerantAdded: parseFloat(refrigerantAdded.value),
        fullSystemCharge: parseFloat(fullSystemCharge.value),
        daysSinceLast: parseFloat(daysSinceLast.value),
    })
})

function handleSubmit (e:Event) {
    e.preventDefault()
    submitted.value = true
}

function handleReset (e:Event) {
    /* substrate-button does not forward type="button", so the inner button
       defaults to submit. Cancel the implicit form submission here. */
    e.preventDefault()
    applianceType.value = 'commercial'
    refrigerantAdded.value = ''
    fullSystemCharge.value = ''
    daysSinceLast.value = ''
    submitted.value = false
    formNonce.value += 1
}

interface GaugeModel {
    valuePct:number;
    thresholdPct:number;
}

function gaugeModel (value:number, threshold:number):GaugeModel {
    const top = Math.max(value, threshold)
    const scaleMax = Math.max(threshold, Math.ceil((top * 1.25) / 5) * 5)
    return {
        valuePct: Math.min(100, (value / scaleMax) * 100),
        thresholdPct: (threshold / scaleMax) * 100,
    }
}

const Gauge:FunctionComponent<{
    value:number;
    threshold:number;
    exceeds:boolean;
}> = function ({ value, threshold, exceeds }) {
    const { valuePct, thresholdPct } = gaugeModel(value, threshold)
    const status = exceeds ? 'exceeds' : 'compliant'
    const label = `Leak rate ${value.toFixed(1)} percent against the ` +
        `EPA limit of ${threshold} percent ` + EM_DASH + ' ' +
        `${exceeds ? 'over the limit' : 'within the limit'}.`

    return html`<div
        class="lrc-gauge"
        data-status=${status}
        style=${`--value-pct:${valuePct};--threshold-pct:${thresholdPct}`}
        role="img"
        aria-label=${label}
    >
        <div class="lrc-gauge-track">
            <div class="lrc-gauge-fill"></div>
        </div>
        <div class="lrc-gauge-mark"></div>
        <p class="lrc-gauge-caption">EPA limit ${EM_DASH} ${threshold}%</p>
    </div>`
}

export const LeakRateCalculator:FunctionComponent<{
    state:AppState
}> = function LeakRateCalculator () {
    const r = result.value
    const errs = errors.value
    const nonce = formNonce.value

    const status = !r || r.exceedsThreshold === null ?
        'neutral' :
        r.exceedsThreshold ? 'exceeds' : 'compliant'

    const statusText = r === null ?
        '' :
        r.exceedsThreshold === null ?
            'No EPA limit for this appliance type' :
            r.exceedsThreshold ?
                `Exceeds the ${r.threshold}% limit` :
                `Within the ${r.threshold}% limit`

    return html`
        <${ToolPage}
            eyebrow="EPA Section 608"
            title="Leak Rate Calculator"
            description="Compute an annualized refrigerant leak rate and check it
                against the EPA Section 608 threshold for your appliance type."
        >
            <${ToolWorkbench}
                emptyHint="Enter the system values and select Calculate to
                    check the leak rate against the EPA limit."
            >
                <form class="tool-form" onSubmit=${handleSubmit} novalidate>
                <div class="tool-field">
                    <label class="tool-field-label" for="lrc-appliance">
                        Appliance type
                    </label>
                    <select
                        id="lrc-appliance"
                        class="tool-select"
                        value=${applianceType.value}
                        onChange=${(e:Event) => {
                            applianceType.value =
                                (e.target as HTMLSelectElement)
                                    .value as ApplianceType
                        }}
                    >
                        ${Object.entries(APPLIANCE_LABELS).map(([val, label]) =>
                            html`<option key=${val} value=${val}>
                                ${label}
                            </option>`
                        )}
                    </select>
                    <p class="tool-field-hint">
                        Sets the leak-rate threshold the EPA applies.
                    </p>
                </div>

                <div class="tool-grid">
                    <div class=${'tool-field' +
                        (errs.refrigerantAdded ? ' has-error' : '')}>
                        <substrate-input
                            key=${`added-${nonce}`}
                            label="Refrigerant added (lbs)"
                            type="number"
                            inputmode="decimal"
                            min="0"
                            step="any"
                            placeholder="5"
                            value=${refrigerantAdded.value}
                            aria-invalid=${errs.refrigerantAdded ?
                                'true' : 'false'}
                            onInput=${(e:Event) => {
                                refrigerantAdded.value =
                                    (e.target as HTMLInputElement).value
                            }}
                        ></substrate-input>
                        ${errs.refrigerantAdded ?
                            html`<p class="tool-field-error">
                                ${errs.refrigerantAdded}
                            </p>` :
                            html`<p class="tool-field-hint">
                                Pounds added at the last top-up.
                            </p>`
                        }
                    </div>

                    <div class=${'tool-field' +
                        (errs.fullSystemCharge ? ' has-error' : '')}>
                        <substrate-input
                            key=${`charge-${nonce}`}
                            label="Full system charge (lbs)"
                            type="number"
                            inputmode="decimal"
                            min="0"
                            step="any"
                            placeholder="50"
                            value=${fullSystemCharge.value}
                            aria-invalid=${errs.fullSystemCharge ?
                                'true' : 'false'}
                            onInput=${(e:Event) => {
                                fullSystemCharge.value =
                                    (e.target as HTMLInputElement).value
                            }}
                        ></substrate-input>
                        ${errs.fullSystemCharge ?
                            html`<p class="tool-field-error">
                                ${errs.fullSystemCharge}
                            </p>` :
                            html`<p class="tool-field-hint">
                                Total pounds at a full charge.
                            </p>`
                        }
                    </div>

                    <div class=${'tool-field' +
                        (errs.daysSinceLast ? ' has-error' : '')}>
                        <substrate-input
                            key=${`days-${nonce}`}
                            label="Days since last addition"
                            type="number"
                            inputmode="numeric"
                            min="1"
                            step="1"
                            placeholder="180"
                            value=${daysSinceLast.value}
                            aria-invalid=${errs.daysSinceLast ?
                                'true' : 'false'}
                            onInput=${(e:Event) => {
                                daysSinceLast.value =
                                    (e.target as HTMLInputElement).value
                            }}
                        ></substrate-input>
                        ${errs.daysSinceLast ?
                            html`<p class="tool-field-error">
                                ${errs.daysSinceLast}
                            </p>` :
                            html`<p class="tool-field-hint">
                                Calendar days since the previous addition.
                            </p>`
                        }
                    </div>
                </div>

                <div class="tool-actions">
                    <substrate-button class="primary" type="submit">
                        Calculate
                    </substrate-button>
                    <substrate-button
                        type="button"
                        onClick=${handleReset}
                    >
                        Reset
                    </substrate-button>
                </div>
            </form>

            ${r && html`
                <div class="tool-readout" data-status=${status}>
                    <p class="tool-readout-label">Annualized leak rate</p>
                    <p class="tool-readout-value">
                        ${r.leakRatePercent.toFixed(1)}<span
                            class="tool-readout-unit"
                        >%</span>
                    </p>
                    <p class="tool-status" data-status=${status}>
                        ${statusText}
                    </p>

                    ${r.threshold !== null && html`
                        <${Gauge}
                            value=${r.leakRatePercent}
                            threshold=${r.threshold}
                            exceeds=${r.exceedsThreshold === true}
                        />
                    `}

                    ${r.repairWindowDays ?
                        html`<p class="tool-readout-detail">
                            A ${r.repairWindowDays}-day repair window applies
                            under EPA Section 608 Subpart F.${' '}
                            ${r.industrialExtensionAvailable ?
                                'Industrial process refrigeration may qualify ' +
                                'for an extension.' :
                                ''}
                        </p>` :
                        r.threshold === null ?
                            html`<p class="tool-readout-detail">
                                EPA Section 608 does not set a leak-rate
                                threshold for this appliance type.
                            </p>` :
                            html`<p class="tool-readout-detail">
                                Below the EPA action threshold for
                                ${' '}${APPLIANCE_LABELS[applianceType.value]
                                    .toLowerCase()}.
                            </p>`
                    }
                </div>
            `}
            </${ToolWorkbench}>

            <${Disclaimer} />
        </${ToolPage}>
    `
}
